import axios from "axios";
import {
    RCHANS_URL,
    RCHAN_KEYS_URL,
    RCHAN_USERS_URL,
    RNS_URL,
    buildServiceParamMap,
    mergeById,
    buildMapOfListsByAttribute,
    buildListOfValues,
    buildMapByValueName,
    buildMapOfListsByValueName,
    RRS_URL
} from "../../common";
import { userInfo } from "../../common/user-info"
import { authHeader } from "../../common/auth-header"
import { className } from "postcss-selector-parser";
import jwtInterceptor  from '../../common/jwt-interceptor';

const state = {
   channelsByRecipientUserId:{},
   channelsById:{},
   privateMessageRecipients: [],
   privateMessageRecipientsByChannelId: {},
   rallyChannels: [],
   privateChannels: {},
   newPrivateMessages:[],
   newPrivateMessageCountBySenderName:{},
   // Thread-centric state (keyed by channel id, not recipient user id) —
   // covers both 1:1 PrivateMessage and N-user GroupMessage channels.
   threadsById: {},
   // Server-derived unread state (rns): channel id -> count of messages with
   // no 'viewed' user-activity for this user. Channels with zero unread are
   // omitted, so `(map[id] || 0) > 0` is the unread test.
   unreadCountByChannelId: {}
};

// Builds a thread record from a raw JSON:API channel. A thread is a channel
// plus the participant user ids (excluding the current user).
function buildThread(channel, myUserId) {
  const channelUsers = channel.relationships?.['channel-users']?.data || [];
  const participantUserIds = channelUsers
    .map(cu => cu['user-id'])
    .filter(uid => uid && parseInt(uid) !== parseInt(myUserId));
  return {
    id: channel.id + "",
    type: channel.attributes?.type,
    isGroup: channel.attributes?.type === 'GroupMessage',
    rallyId: channel.attributes?.['rally-id'],
    participantUserIds,
    channel
  };
}

const getters = {
  //rallyChannels: state => state.rallyChannel
  channelsByRecipientUserId: state => state.channelsByUserId,
  privateMessageRecipients: state => state.privateMessageRecipients,
  privateMessageRecipientsByChannelId: state => state.privateMessageRecipientsByChannelId,
  rallyChannels: state => state.rallyChannels,
  privateChannels: state => state.privateChannels,
  newPrivateMessages: state=>state.newPrivateMessages,
  newPrivateMessageCountBySenderName: state=>state.newPrivateMessageCountBySenderName,
  threadsById: state => state.threadsById,
  threads: state => Object.values(state.threadsById),
  unreadCountByChannelId: state => state.unreadCountByChannelId,
  // Drives the dot on the top-nav Messaging icon: true while ANY thread has
  // unread messages; clears only when every thread has been read.
  hasUnreadThreads: state => Object.values(state.unreadCountByChannelId).some(c => c > 0)
};



const actions = {
    //api/v1/channels?page%5Bnumber%5D=1&page%5Bsize%5D=100&channel_users=22812&type=PrivateMessage”
    

    async fetchUserChannels({commit, state}, inparams){
      const { userId } = inparams;
      let jsonparams = {
        channel_users: userId,
        type:"PrivateMessage",
        "page[number]": 1,
        "page[size]":100
      };
      let params = buildServiceParamMap(jsonparams);
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json"
        },
        url: RCHANS_URL(),
        params
      };
      const response = await jwtInterceptor(requestOptions);
      let byIdMap = {}
      let byUserIdMap = {}
      let recipientsByChannelId = {}
      let recipients = []
      response.data.data.forEach(c => {
        if (c && c.attributes){
            byIdMap[c.id] = c
          byUserIdMap = buildMapOfListsByValueName(c.relationships['channel-users'].data, 'user-id', byUserIdMap)
          if (c.relationships['channel-users'].data && c.relationships['channel-users'].data.length > 0){
            recipients = [ ...recipients, ...c.relationships['channel-users'].data]
            //filter out my user id...it becomes problematic!!
            recipients = recipients.filter((r) => {
              return (parseInt(r['user-id']) !== parseInt(userId)) && r['user-id'];
            });
          }
        }
      });
      let userIdsByRallyId = buildMapOfListsByAttribute(response.data.data, "rally-id")

      commit("setChannelsByRecipientUserId", {byUserIdMap, recipients})
      commit("setChannelsById", {byIdMap})
      let userIds = Object.keys(byIdMap)
      if (userIds && userIds.length > 0){
        userIds = userIds.filter(function(s) {
        return s !== 'undefined'
        });
      }
      let rallyIds = Object.keys(userIdsByRallyId)
      if (rallyIds && rallyIds.length > 0){
        for (const rid of rallyIds) {
          let userIds = buildListOfValues("id", userIdsByRallyId[rid]);
          if (userIds && userIds.length > 0) {
             this.dispatch("memberships/fetchMembersByRallyAndUserId", { rallyId: rid, userIds });
          }
        }
      }

    },

    // Fetches every messaging channel the user belongs to — both 1:1
    // (PrivateMessage) and group (GroupMessage) — and builds the
    // thread-centric store keyed by channel id. Also refreshes the legacy
    // recipient-keyed state from the PrivateMessage results so older
    // components keep working during the migration.
    async fetchUserThreads({commit, state}, inparams){
      const { userId } = inparams;
      const fetchType = async (type) => {
        let jsonparams = {
          channel_users: userId,
          type,
          "page[number]": 1,
          "page[size]": 100
        };
        const requestOptions = {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          url: RCHANS_URL(),
          params: buildServiceParamMap(jsonparams)
        };
        try {
          const response = await jwtInterceptor(requestOptions);
          return response.data.data || [];
        } catch (e) {
          console.log(`fetchUserThreads(${type}) failed`, e);
          return [];
        }
      };

      const [privateChannels, groupChannels] = await Promise.all([
        fetchType('PrivateMessage'),
        fetchType('GroupMessage')
      ]);
      const allChannels = [...privateChannels, ...groupChannels];

      // Thread store (both types).
      const threads = allChannels
        .filter(c => c && c.attributes)
        .map(c => buildThread(c, userId));
      commit("setThreads", { threads });

      // channelsById for both types (used to attach privateMessageChannel
      // onto fetched contents).
      let byIdMap = {};
      allChannels.forEach(c => { if (c && c.attributes) { byIdMap[c.id] = c; } });
      commit("setChannelsById", {byIdMap});

      // Legacy recipient-keyed state — PrivateMessage channels only, exactly
      // as fetchUserChannels built it.
      let byUserIdMap = {};
      let recipients = [];
      privateChannels.forEach(c => {
        if (c && c.attributes){
          byUserIdMap = buildMapOfListsByValueName(c.relationships['channel-users'].data, 'user-id', byUserIdMap);
          if (c.relationships['channel-users'].data && c.relationships['channel-users'].data.length > 0){
            recipients = [ ...recipients, ...c.relationships['channel-users'].data];
            recipients = recipients.filter((r) => {
              return (parseInt(r['user-id']) !== parseInt(userId)) && r['user-id'];
            });
          }
        }
      });
      commit("setChannelsByRecipientUserId", {byUserIdMap, recipients});

      // Hydrate participant names/avatars per rally (all thread members,
      // both types).
      let userIdsByRallyId = {};
      allChannels.forEach(c => {
        if (!c || !c.attributes) { return; }
        const rid = c.attributes['rally-id'];
        if (!rid) { return; }
        const cu = c.relationships?.['channel-users']?.data || [];
        userIdsByRallyId[rid] = [ ...(userIdsByRallyId[rid] || []), ...cu.map(u => u['user-id']).filter(u => u) ];
      });
      for (const rid of Object.keys(userIdsByRallyId)) {
        const userIds = [...new Set(userIdsByRallyId[rid])];
        if (userIds.length > 0) {
          this.dispatch("memberships/fetchMembersByRallyAndUserId", { rallyId: rid, userIds });
        }
      }
    },

    // Find-or-create the thread channel for a set of recipients in a rally.
    // 1 recipient -> PrivateMessage (invariantly 2-user); 2+ recipients ->
    // GroupMessage. The channel service resumes an existing channel when the
    // exact member set already has one. Returns the thread (or null).
    async fetchOrCreateThread({commit, state}, inparams){
      const user = userInfo();
      const { rallyId, recipientUserIds } = inparams;
      const memberIds = [...new Set([user.user_id + "", ...recipientUserIds.map(id => id + "")])];
      const type = memberIds.length === 2 ? 'PrivateMessage' : 'GroupMessage';
      const jsonparams = {
        type,
        // Send as an array (channel_users[]=...) — the same wire format the
        // legacy fetchPrivateChannels flow uses, which ChannelAuthorizer's
        // listable_by? Array branch is known to accept.
        channel_users: memberIds,
        rally_id: rallyId,
        creator_id: user.user_id,
        "page[number]": 1,
        "page[size]": 100
      };
      const requestOptions = {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        url: RCHANS_URL(),
        params: buildServiceParamMap(jsonparams)
      };
      const response = await jwtInterceptor(requestOptions);
      const channel = response.data.data && response.data.data[0];
      if (!channel) { return null; }
      const thread = buildThread(channel, user.user_id);
      // The find-or-create response may omit channel-users relationships on
      // freshly created channels; fall back to the requested recipient set.
      if (!thread.participantUserIds || thread.participantUserIds.length === 0) {
        thread.participantUserIds = recipientUserIds.map(id => id + "");
      }
      commit("upsertThread", { thread });
      commit("setChannelsById", { byIdMap: { [channel.id]: channel } });
      return thread;
    },

    // Adds members to an existing GroupMessage channel ("+ Add Member").
    // Note: 1:1 PrivateMessage channels reject a third member server-side;
    // callers must create a new group thread instead (fetchOrCreateThread
    // with the union member set).
    async addChannelUsers({commit, state}, inparams){
      const { channelId, userIds } = inparams;
      const requestOptions = {
        method: "POST",
        url: RCHAN_USERS_URL(),
        headers: { "Content-Type": "application/json" },
        // channel_id must ALSO be a top-level (query) param:
        // ChannelUserAuthorizer resolves the channel from request params
        // during authorization, and the JSON:API body attributes aren't
        // visible there — without this the POST is rejected as Forbidden.
        params: buildServiceParamMap({ channel_id: channelId }),
        data: {
          data: {
            type: "channel-users",
            attributes: {
              channel_id: channelId,
              user_id: userIds
            }
          }
        }
      };
      await jwtInterceptor(requestOptions);
    },

    // Removes one member from a thread channel. Self-removal = leaving the
    // conversation (allowed for any member); removing someone else is
    // restricted server-side to the channel creator
    // (ChannelUserAuthorizer#deletable_by?).
    async removeChannelUser({commit, state}, inparams){
      const { thread, userId } = inparams;
      const me = userInfo().user_id + "";
      const cus = thread.channel?.relationships?.['channel-users']?.data || [];
      const cu = cus.find(c => (c['user-id'] + "") === (userId + ""));
      if (!cu || !cu.id) { throw new Error("channel_user not found for user " + userId); }
      const requestOptions = {
        method: "DELETE",
        // channel_id param is required: ChannelUserAuthorizer resolves the
        // channel from it during authorization.
        url: RCHAN_USERS_URL() + "/" + cu.id,
        headers: { "Content-Type": "application/json" },
        params: buildServiceParamMap({ channel_id: thread.id })
      };
      await jwtInterceptor(requestOptions);
      if ((userId + "") === me) {
        commit("removeThread", { threadId: thread.id });
        return;
      }
      const updated = {
        ...thread,
        participantUserIds: thread.participantUserIds.filter(uid => (uid + "") !== (userId + "")),
        channel: {
          ...thread.channel,
          relationships: {
            ...thread.channel.relationships,
            'channel-users': { data: cus.filter(c => (c['user-id'] + "") !== (userId + "")) }
          }
        }
      };
      commit("upsertThread", { thread: updated });
    },

    // Renames a thread's channel (custom group display name). Any member may
    // rename: the channel is members_write 'private' and every member is a
    // channel_user, which passes ChannelAuthorizer#writable_by?.
    async renameThread({commit, state}, inparams){
      const { thread, name } = inparams;
      const requestOptions = {
        method: "PUT",
        url: RCHANS_URL() + "/" + thread.id,
        headers: { "Content-Type": "application/json" },
        data: { data: { type: "channels", attributes: { id: thread.id, name } } }
      };
      await jwtInterceptor(requestOptions);
      const updated = {
        ...thread,
        channel: {
          ...thread.channel,
          attributes: { ...thread.channel.attributes, name }
        }
      };
      commit("upsertThread", { thread: updated });
    },

    // Deletes a thread's channel (soft delete server-side — the channel is
    // acts_as_paranoid and removes its channel_users with it). This removes
    // the conversation for ALL participants; there is no per-user hide.
    async deleteThread({commit, state}, inparams){
      const { threadId } = inparams;
      const requestOptions = {
        method: "DELETE",
        url: RCHANS_URL() + "/" + threadId,
        headers: { "Content-Type": "application/json" }
      };
      await jwtInterceptor(requestOptions);
      commit("removeThread", { threadId });
    },

    // Refreshes per-thread unread counts from rns for every thread currently
    // in the store (call after fetchUserThreads so the id list is fresh).
    // Pass zeroChannelId for the thread open on screen: the user is looking
    // at it, so it must never show as unread even while the async 'viewed'
    // writes (Resque) are still catching up server-side.
    async fetchUnreadCounts({commit, state}, inparams){
      const zeroChannelId = inparams && inparams.zeroChannelId;
      const channelIds = Object.keys(state.threadsById);
      if (channelIds.length === 0) {
        commit("setUnreadCounts", { counts: {} });
        return;
      }
      const requestOptions = {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        url: RNS_URL() + "/channel-counts/user-count",
        params: buildServiceParamMap({ channel_ids: channelIds })
      };
      try {
        const response = await jwtInterceptor(requestOptions);
        const counts = { ...((response.data && response.data.unread_counts) || {}) };
        if (zeroChannelId) { delete counts[zeroChannelId + ""]; }
        commit("setUnreadCounts", { counts });
      } catch (e) {
        // Endpoint not deployed yet / transient failure: keep current state.
        console.log("fetchUnreadCounts failed", e);
      }
    },

    // Marks every message in a thread as viewed for the current user (rns
    // writes the same 'viewed' user-activities the per-message observer
    // does). Optimistically zeroes the local count so the dots clear
    // immediately on open.
    async markThreadRead({commit, state}, inparams){
      const { threadId } = inparams;
      commit("setUnreadCount", { channelId: threadId, count: 0 });
      const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        url: RNS_URL() + "/channel-counts/mark-read",
        data: { channel_id: threadId, source: 'vue-mobile' }
      };
      try {
        await jwtInterceptor(requestOptions);
      } catch (e) {
        console.log("markThreadRead failed", e);
      }
    },

    async addNewPrivateMessage({commit, state}, inparams){
      commit("setNewPrivateMessage", inparams);
    },

    async clearNewPrivateMessages({commit, state}, inparams){
      commit("clearNewPrivateMessages");
    },

    async clearPrivateMessageCountByUserName({commit, state}, inparams){
      commit("clearPrivateMessageCountByUserName", inparams);
    },

    async fetchChannelKey({commit, state}, inparams){
      const user = userInfo();
      const { channelId , channelKey} = inparams;
      var jsonparams = {
          channel_id: channelId,
          key: channelKey,
      };
      var params = buildServiceParamMap(jsonparams);
      const requestOptions = {
          method: "GET",
          headers: {
              "Content-Type": "application/json"
          },
          url: RCHAN_KEYS_URL(), //+ "/" + channelId + ".json",
          params
      };
      const response = await jwtInterceptor(requestOptions);
      //const channel_key = (rallyId + "").concat(':').concat(channelUsers[1]);

      //commit("setPrivateChannels", { channel_key , channels: [response.data.data]});
    },

    async fetchPrivateChannels({commit, state}, inparams){
      const user = userInfo();
      const { rallyId, channelUsers } = inparams;
      var jsonparams = {
          type: 'PrivateMessage',
          channel_users: channelUsers,
          rally_id: rallyId,
          sort: "sort_order",
          "page[number]": 1,
          "page[size]":100
      };
      var params = buildServiceParamMap(jsonparams);
      const requestOptions = {
          method: "GET",
          headers: {
              "Content-Type": "application/json"
          },
          url: RCHANS_URL(),
          params
      };
      const response = await jwtInterceptor(requestOptions);
      const channel_key = (rallyId + "").concat(':').concat(channelUsers[1]);

      commit("setPrivateChannels", { channel_key , channels: [response.data.data]});
    },

    async setChannelsById({commit, state}, inparams){
      commit("setChannelsById", inparams)
    },
    async updateChannel({ commit, state }, inparams) {
        const { channel } = inparams;
        const user = userInfo();
        const updatedAttributes = {
            id: channel.id,
        };
        const requestOptions = {
            method: "PUT",
            url: RCHANS_URL() + "/" + channel.id,
            headers: {
                "Content-Type": "application/json"
            },
            data: {
                data: {
                    type: "channels",
                    attributes: channel.attributes
                }
            }
        };
        const response = await jwtInterceptor(requestOptions);
        this.dispatch('navigation/refreshCurrentRallyData')
    },

};
const mutations = {
    setChannelsByRecipientUserId: (state, params ) => {
      const { byUserIdMap, recipients} = params
        state.channelsByRecipientUserId = {...state.channelsByRecipientUserId, ...byUserIdMap}
        let pmr = mergeById(state.privateMessageRecipients, recipients, "user-id" )
        state.privateMessageRecipientsByChannelId = {...state.privateMessageRecipientsByChannelId, ...buildMapByValueName("channel-id", pmr)}
        state.privateMessageRecipients  = [...pmr]
    },
    setChannelsById: (state, params ) => {
      const { byIdMap} = params
        state.channelsById = {...state.channelsById, ...byIdMap}
    },
    setThreads: (state, params) => {
      const { threads } = params
      const byId = {}
      threads.forEach(t => { byId[t.id] = t })
      state.threadsById = byId
    },
    upsertThread: (state, params) => {
      const { thread } = params
      state.threadsById = { ...state.threadsById, [thread.id]: thread }
    },
    removeThread: (state, params) => {
      const { threadId } = params
      const byId = { ...state.threadsById }
      delete byId[threadId + ""]
      state.threadsById = byId
    },
    setUnreadCounts: (state, params) => {
      const { counts } = params
      const normalized = {}
      Object.keys(counts).forEach(k => {
        normalized[k + ""] = parseInt(counts[k]) || 0
      })
      state.unreadCountByChannelId = normalized
    },
    setUnreadCount: (state, params) => {
      const { channelId, count } = params
      state.unreadCountByChannelId = { ...state.unreadCountByChannelId, [channelId + ""]: count }
    },
    setNewPrivateMessage: (state, params ) => {
      const { message } = params
        state.newPrivateMessages = [ message, ...state.newPrivateMessages]
        let senderName = message.title.split("from ")[1].split(":")[0]
        let senderCount = state.newPrivateMessageCountBySenderName[senderName]
        if (!senderCount){
          senderCount = 1
        } else {
          senderCount++
        }
        let newMap = {}
        newMap[senderName] = senderCount
        state.newPrivateMessageCountBySenderName = {...state.newPrivateMessageCountBySenderName, ...newMap}
    },
    clearNewPrivateMessages: (state, params ) => {
        state.newPrivateMessages = []
    },
    clearPrivateMessageCountByUserName: (state, params ) => {
      const { name } =  params
      let newMap = {} 
      newMap[name] = 0
      state.newPrivateMessageCountBySenderName = {...state.newPrivateMessageCountBySenderName, ...newMap}
    },

    setRallyChannels: (state, rChannels) =>
      (state.rallyChannels = rChannels),
    setPrivateChannels: (state, params) => {
        const {channel_key, channels} = params;
        var byChannelKeyMap = {};

        byChannelKeyMap[channel_key] = channels[0][0];

        state.privateChannels = { ...byChannelKeyMap};
    }
    //not used currently
    /*setRallyChannels: (state, rChannels) =>
      (state.rallyChannels = rChannels)*/
};
  
  export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};